import { useState } from "react";
import axios from "axios";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIALTY_OPTIONS = [
    { value: "Cardiology", key: "cardiology" },
    { value: "Orthopaedics", key: "orthopaedics" },
    { value: "Oncology", key: "oncology" },
    { value: "Fertility", key: "fertility" },
    { value: "Other / not sure yet", key: "other" },
];

const initialForm = {
    name: "",
    email: "",
    phone: "",
    country: "",
    specialty: "",
    message: "",
    consent: false,
};

// Shared enquiry form — used by Medical Opinion, Second Opinion, Treatment Planning and Contact.
export const EnquiryForm = ({ service, submitLabel = "Submit Enquiry", testIdPrefix = "enquiry-form" }) => {
    const { t, lang } = useLang();
    const [form, setForm] = useState(initialForm);
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [reference, setReference] = useState("");
    const [errorMsg, setErrorMsg] = useState("");

    const set = (key) => (e) =>
        setForm((f) => ({
            ...f,
            [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
        }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus("loading");
        setErrorMsg("");
        try {
            const res = await axios.post(`${API}/enquiries`, {
                service,
                preferred_language: lang,
                ...form,
            });
            const ref = res.data?.case_id || "";
            setReference(ref);
            setStatus("success");
        } catch (err) {
            setStatus("error");
            setErrorMsg(
                t("form.errorMsg", "Something went wrong while sending your enquiry. Please try again, or reach us on WhatsApp.")
            );
        }
    };

    if (status === "success") {
        return (
            <div
                data-testid={`${testIdPrefix}-success`}
                className="bg-white border border-pine/15 rounded-2xl p-8 sm:p-12 text-center"
            >
                <span className="mx-auto flex items-center justify-center w-14 h-14 rounded-full bg-sage text-forest">
                    <Check className="w-6 h-6" />
                </span>
                <h3 className="font-serif text-3xl mt-6">{t("form.enquiryReceived", "Enquiry received")}</h3>
                <p className="mt-3 text-moss leading-relaxed max-w-md mx-auto">
                    {t("form.successNote", "Thank you for trusting UniversHeal. Our team will review your details and reach out to you shortly.")}
                </p>
                {reference && (
                    <p data-testid={`${testIdPrefix}-reference`} className="mt-5 inline-block bg-sage text-forest text-sm font-bold tracking-wider px-4 py-2 rounded-full">
                        {t("form.yourReference", "Your reference:")} {reference}
                    </p>
                )}
                <button
                    type="button"
                    data-testid={`${testIdPrefix}-reset`}
                    onClick={() => {
                        setForm(initialForm);
                        setStatus("idle");
                    }}
                    className="block mx-auto mt-8 text-sm font-semibold text-pine underline underline-offset-4 hover:text-forest"
                >
                    {t("form.sendAnother", "Send another enquiry")}
                </button>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            data-testid={testIdPrefix}
            className="bg-white border border-pine/15 rounded-2xl p-6 sm:p-10 shadow-[0_20px_60px_rgba(6,60,53,0.06)]"
            noValidate={false}
        >
            <div className="grid sm:grid-cols-2 gap-5">
                <div>
                    <label htmlFor={`${testIdPrefix}-name`} className="label-field">
                        {t("form.fullName", "Full name *")}
                    </label>
                    <input
                        id={`${testIdPrefix}-name`}
                        data-testid={`${testIdPrefix}-name`}
                        required
                        value={form.name}
                        onChange={set("name")}
                        className="input-field"
                        placeholder={t("form.namePlaceholder", "Your full name")}
                        autoComplete="name"
                    />
                </div>
                <div>
                    <label htmlFor={`${testIdPrefix}-email`} className="label-field">
                        {t("form.email", "Email *")}
                    </label>
                    <input
                        id={`${testIdPrefix}-email`}
                        data-testid={`${testIdPrefix}-email`}
                        type="email"
                        required
                        value={form.email}
                        onChange={set("email")}
                        className="input-field"
                        placeholder="you@example.com"
                        autoComplete="email"
                    />
                </div>
                <div>
                    <label htmlFor={`${testIdPrefix}-phone`} className="label-field">
                        {t("form.phone", "Phone / WhatsApp")}
                    </label>
                    <input
                        id={`${testIdPrefix}-phone`}
                        data-testid={`${testIdPrefix}-phone`}
                        type="tel"
                        value={form.phone}
                        onChange={set("phone")}
                        className="input-field"
                        placeholder={t("form.phonePlaceholder", "Include country code")}
                        autoComplete="tel"
                    />
                </div>
                <div>
                    <label htmlFor={`${testIdPrefix}-country`} className="label-field">
                        {t("form.country", "Country of residence")}
                    </label>
                    <input
                        id={`${testIdPrefix}-country`}
                        data-testid={`${testIdPrefix}-country`}
                        value={form.country}
                        onChange={set("country")}
                        className="input-field"
                        placeholder={t("form.countryPlaceholder", "Where are you writing from?")}
                        autoComplete="country-name"
                    />
                </div>
                <div className="sm:col-span-2">
                    <label htmlFor={`${testIdPrefix}-specialty`} className="label-field">
                        {t("form.areaOfCare", "Area of care")}
                    </label>
                    <select
                        id={`${testIdPrefix}-specialty`}
                        data-testid={`${testIdPrefix}-specialty`}
                        value={form.specialty}
                        onChange={set("specialty")}
                        className="input-field"
                    >
                        <option value="">{t("form.selectSpecialty", "Select a specialty (optional)")}</option>
                        {SPECIALTY_OPTIONS.map((s) => (
                            <option key={s.key} value={s.value}>
                                {t(`form.specialty.${s.key}`, s.value)}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="sm:col-span-2">
                    <label htmlFor={`${testIdPrefix}-message`} className="label-field">
                        {service === "General Enquiry"
                            ? t("form.messageLabelGeneral", "How can we help? *")
                            : t("form.messageLabelCondition", "Tell us about your condition *")}
                    </label>
                    <textarea
                        id={`${testIdPrefix}-message`}
                        data-testid={`${testIdPrefix}-message`}
                        required
                        rows={5}
                        value={form.message}
                        onChange={set("message")}
                        className="input-field resize-y"
                        placeholder={
                            service === "General Enquiry"
                                ? t("form.messagePlaceholderGeneral", "Share a few details so we can point you in the right direction.")
                                : t("form.messagePlaceholderCondition", "A short summary of the diagnosis or concern, treatments already considered, and any questions you have. Medical reports can be shared securely after your enquiry.")
                        }
                    />
                </div>
            </div>

            <label
                htmlFor={`${testIdPrefix}-consent`}
                className="flex items-start gap-3 mt-6 text-sm text-moss leading-relaxed cursor-pointer"
            >
                <input
                    id={`${testIdPrefix}-consent`}
                    data-testid={`${testIdPrefix}-consent`}
                    type="checkbox"
                    required
                    checked={form.consent}
                    onChange={set("consent")}
                    className="mt-0.5 w-5 h-5 rounded border-pine/30 text-forest focus:ring-pine/30 cursor-pointer"
                />
                {t("form.consent", "I agree that UniversHeal may contact me about this enquiry. I understand that UniversHeal is a healthcare coordination service and does not provide medical treatment.")}
            </label>

            {status === "error" && (
                <p
                    role="alert"
                    data-testid={`${testIdPrefix}-error`}
                    className="mt-5 text-sm font-semibold text-red-800 bg-red-50 border border-red-200 rounded-xl px-4 py-3"
                >
                    {errorMsg}
                </p>
            )}

            <button
                type="submit"
                data-testid={`${testIdPrefix}-submit`}
                disabled={status === "loading"}
                className="btn-primary w-full sm:w-auto mt-8 group disabled:opacity-60 disabled:cursor-not-allowed"
            >
                {status === "loading" ? (
                    <>
                        {t("form.sending", "Sending")}
                        <Loader2 className="w-4 h-4 animate-spin" />
                    </>
                ) : (
                    <>
                        {submitLabel}
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                )}
            </button>
        </form>
    );
};
